import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import { canNavigateModule, can as checkPermission } from '../../utils/permissions';
import {
  ClipboardList,
  Tags,
  Palette,
  Sparkles,
  Package,
  Truck,
  PanelLeftOpen,
  PanelLeftClose,
  ChevronDown,
  UserCog,
  Table2,
} from 'lucide-react';
import './Sidebar.css';

/**
 * Itens estáticos; visibilidade é filtrada por permissão (módulo ou chave).
 * moduleKey / moduleKeys usam canNavigateModule (ex.: products.view, products.update…).
 */
const MENU_ITEMS = [
  {
    label: 'Gerenciar Usuários',
    path: '/users-management',
    icon: UserCog,
    permissionsAny: ['users_management.view', 'invitations.view'],
  },
  {
    label: 'Leads',
    path: '/leads',
    icon: ClipboardList,
    permission: 'leads.view',
  },
  {
    label: 'Produtos',
    path: null,
    icon: Package,
    children: [
      { label: 'Todos', path: '/products', icon: Package, moduleKey: 'products' },
      {
        label: 'Categorias',
        path: '/categories',
        icon: Tags,
        moduleKeys: ['categories', 'subcategories'],
      },
      { label: 'Cores', path: '/colors', icon: Palette, moduleKey: 'colors' },
      {
        label: 'Tipos de personalização',
        path: '/personalization-types',
        icon: Sparkles,
        moduleKey: 'personalization_types',
      },
      {
        label: 'Matrizes de preços',
        path: '/pricing-matrices',
        icon: Table2,
        moduleKey: 'pricing_matrix',
      },
    ],
  },
  {
    label: 'Fornecedores',
    path: '/suppliers',
    icon: Truck,
    moduleKey: 'suppliers',
  },
];

const childIsVisible = (child, user) => {
  if (child.moduleKeys?.length) {
    return child.moduleKeys.some(k => canNavigateModule(user, k));
  }
  if (child.moduleKey) {
    return canNavigateModule(user, child.moduleKey);
  }
  return true;
};

const buildVisibleMenu = user =>
  MENU_ITEMS.map(item => {
    if (!item.children?.length) {
      return item;
    }
    return {
      ...item,
      children: item.children.filter(c => childIsVisible(c, user)),
    };
  }).filter(item => {
    if (Array.isArray(item.children)) {
      return item.children.length > 0;
    }
    if (item.moduleKeys?.length) {
      return item.moduleKeys.some(k => canNavigateModule(user, k));
    }
    if (item.moduleKey) {
      return canNavigateModule(user, item.moduleKey);
    }
    if (item.permissionsAny?.length) {
      return item.permissionsAny.some(p => checkPermission(user, p));
    }
    if (item.permission) {
      return checkPermission(user, item.permission);
    }
    return true;
  });

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('sidebar-collapsed') !== 'false';
  });
  const [expandedMenus, setExpandedMenus] = useState({ Produtos: true });

  const { user } = usePermissions();

  const menuItems = useMemo(() => buildVisibleMenu(user), [user]);

  const toggleMenu = label => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('sidebar-collapsed', isCollapsed ? 'true' : 'false');
  }, [isCollapsed]);

  const isActive = path => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentOrChildActive = item => {
    if (item.path && isActive(item.path)) return true;
    if (item.children) {
      return item.children.some(child => isActive(child.path));
    }
    return false;
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  return (
    <>
      <aside
        className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isCollapsed ? 'sidebar--collapsed' : ''}`}
      >
        <div className="sidebar__toggle-wrapper">
          <button
            type="button"
            className="sidebar__toggle"
            onClick={handleToggleCollapse}
            aria-label={isCollapsed ? 'Exibir nomes do menu' : 'Compactar menu'}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            <span className="sidebar__toggle-label">
              {isCollapsed ? 'Exibir nomes' : 'Compactar'}
            </span>
          </button>
        </div>

        <nav className="sidebar__nav">
          <ul className="sidebar__menu">
            {menuItems.map(item => (
              <li
                key={item.path || item.label}
                className={`sidebar__item ${isCollapsed && isParentOrChildActive(item) ? 'sidebar__item--active-collapsed' : ''}`}
              >
                {item.children?.length > 0 && item.path == null ? (
                  isCollapsed ? (
                    <Link
                      to={item.children[0].path}
                      className={`sidebar__link ${isParentOrChildActive(item) ? 'sidebar__link--active' : ''}`}
                      title={item.label}
                      aria-label={item.label}
                      onClick={onClose}
                    >
                      <span className="sidebar__icon">
                        {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                      </span>
                      <span className="sidebar__label">{item.label}</span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className={`sidebar__link sidebar__link--group ${isParentOrChildActive(item) ? 'sidebar__link--active' : ''}`}
                      title={item.label}
                      aria-label={expandedMenus[item.label] ? `Recolher ${item.label}` : `Expandir ${item.label}`}
                      aria-expanded={!!expandedMenus[item.label]}
                      onClick={() => toggleMenu(item.label)}
                    >
                      <span className="sidebar__icon">
                        {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                      </span>
                      <span className="sidebar__label">{item.label}</span>
                      <span className={`sidebar__chevron ${expandedMenus[item.label] ? 'sidebar__chevron--expanded' : ''}`}>
                        <ChevronDown size={18} strokeWidth={2} />
                      </span>
                    </button>
                  )
                ) : (
                  <Link
                    to={item.path}
                    className={`sidebar__link ${isParentOrChildActive(item) ? 'sidebar__link--active' : ''}`}
                    title={isCollapsed ? item.label : undefined}
                    aria-label={item.label}
                    onClick={onClose}
                  >
                    <span className="sidebar__icon">
                      {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                    </span>
                    <span className="sidebar__label">{item.label}</span>
                  </Link>
                )}
                {item.children && item.children.length > 0 && !isCollapsed && expandedMenus[item.label] && (
                  <ul className="sidebar__submenu">
                    {item.children.map(child => (
                      <li key={child.path} className="sidebar__submenu-item">
                        <Link
                          to={child.path}
                          className={`sidebar__submenu-link ${isActive(child.path) ? 'sidebar__submenu-link--active' : ''}`}
                          title={isCollapsed ? child.label : undefined}
                          aria-label={child.label}
                          onClick={onClose}
                        >
                          <span className="sidebar__submenu-icon">
                            {child.icon ? <child.icon size={16} strokeWidth={1.8} /> : <span className="sidebar__submenu-bullet" />}
                          </span>
                          <span className="sidebar__submenu-label">{child.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
